<template>
  <section class="admin-page">

    <!-- Header -->
    <header class="admin-header">
      <div>
        <h1>Creators</h1>
        <p>Review, approve, and manage creator access to the platform.</p>
      </div>
    </header>

    <!-- Filters -->
    <section class="filters">
      <input v-model="filters.search" placeholder="Search by name or email…" />

      <select v-model="filters.status">
        <option value="">All Status</option>
        <option value="pending">Pending</option>
        <option value="active">Active</option>
        <option value="suspended">Suspended</option>
      </select>

      <select v-model="filters.platform">
        <option value="">All Platforms</option>
        <option value="Instagram">Instagram</option>
        <option value="YouTube">YouTube</option>
        <option value="Twitter">X (Twitter)</option>
      </select>
    </section>

    <!-- KPI Row -->
    <section class="kpis">
      <div class="kpi">
        <p>Pending</p>
        <h2>{{ stats.pending }}</h2>
      </div>
      <div class="kpi">
        <p>Active</p>
        <h2>{{ stats.active }}</h2>
      </div>
      <div class="kpi">
        <p>Suspended</p>
        <h2 class="danger">{{ stats.suspended }}</h2>
      </div>
    </section>

    <!-- Creators Table -->
    <section class="card">
      <table class="creator-table">
        <thead>
          <tr>
            <th>Creator</th>
            <th>Username/Platform</th>
            <!-- <th>Platform</th> -->
            <th>Followers</th>
            <th>Risk</th>
            <th>Status</th>
            <th></th>
          </tr>
        </thead>

        <tbody v-if="!loading">
          <tr v-for="c in creators" :key="c.user_id">
            <td>
              <strong>{{ c.user_name }}</strong>
              <div class="sub">{{ c.user_email }}</div>
            </td>
             <td>
              <strong v-if="c.inf!=null && c.inf.inf_username!=null">{{ c.inf.inf_username }}</strong>
              <strong v-else>none</strong>
              <div class="sub">{{ c.inf_platform }}</div>
            </td>

            <!-- <td><span v-if="c.inf!=null">{{ c.inf.inf_platform }}</span></td> -->

            <td><span v-if="c.inf!=null">{{ c.inf.inf_followers }}</span></td>

            <td>
              <span v-if="c.inf!=null" :class="['risk-pill', riskClass(c.inf.inf_risk_score)]">
                {{ c.inf.inf_risk_score }}%
              </span>
            </td>

            <td>
              <span :class="['status-pill', c.status]">
                {{ c.user_status }}
              </span>
            </td>

            <td class="actions">
              <button class="ghost-btn" @click="view(c)">
                Review
              </button>

              <button v-if="c.status === 'pending'" class="primary-btn" @click="approve(c)">
                Approve
              </button>

              <button v-if="c.status === 'active'" class="danger-btn" @click="suspend(c)">
                Suspend
              </button>
            </td>
          </tr>
        </tbody>
      </table>

      <div v-if="!loading && creators.length === 0" class="empty">
        No creators found
      </div>

      <div v-if="loading" class="empty">
        Loading creators...
      </div>

    </section>
    <!-- Pagination -->
    <div v-if="totalPages > 1" class="pagination">
      <button class="ghost-btn" :disabled="page === 1" @click="page--">
        Prev
      </button>

      <span class="page-info">
        Page {{ page }} of {{ totalPages }}
      </span>

      <button class="ghost-btn" :disabled="page === totalPages" @click="page++">
        Next
      </button>

    </div>

  </section>
</template>

<script>
  import { getCreators, getCreatorStats, approveCreator, suspendCreator } from "../apis/infapis";
  import { useToast } from "vue-toastification";

  export default {
    name: "AdminCreators",

    data() {
      return {
        toast: useToast(),

        loading: false,
        page: 1,
        perPage: 10,
        total: 0,

        filters: {
          search: "",
          status: "",
          platform: ""
        },

        stats: {
          pending: 0,
          active: 0,
          suspended: 0
        },

        creators: []
      };
    },

    computed: {
      totalPages() {
        return Math.ceil(this.total / this.perPage);
      }
    },

    watch: {
      filters: {
        deep: true,
        handler() {
          this.page = 1;
          this.loadCreators();
        }
      },
      page() {
        this.loadCreators();
      }
    },

    mounted() {
      this.loadStats();
      this.loadCreators();
    },

    methods: {

      // ================= LOAD STATS =================
      async loadStats() {
        const res = await getCreatorStats();

        if (res.status) {
          this.stats = res.stats;
        }
      },

      // ================= LOAD CREATORS =================
      async loadCreators() {
        this.loading = true;

        const res = await getCreators({
          page_no: this.page - 1,
          search: this.filters.search,
          status: this.filters.status,
          platform: this.filters.platform
        });

        this.loading = false;

        if (!res.status) {
          this.toast.error(res.success || "Failed to load creators");
          return;
        }

        this.creators = res.records || [];
        this.total = parseInt(res.total || 0);
      },

      // ================= RISK CLASS =================
      riskClass(score) {
        score = parseInt(score || 0);

        if (score < 25) return "low";
        if (score < 60) return "medium";
        return "high";
      },

      // ================= VIEW =================
      view(creator) {
        this.$router.push(`/admin/creators/${creator.user_id}`);
      },

      // ================= APPROVE =================
      async approve(creator) {
        const ldataRaw = sessionStorage.getItem("ldata");
        const ldata = ldataRaw ? JSON.parse(ldataRaw) : null;
        const res = await approveCreator(creator.user_id,ldata.user_id);

        if (!res.status) {
          this.toast.error(res.success || "Approval failed");
          return;
        }

        this.toast.success("Creator approved");

        this.loadStats();
        this.loadCreators();
      },

      // ================= SUSPEND =================
      async suspend(creator) {
        const ldataRaw = sessionStorage.getItem("ldata");
        const ldata = ldataRaw ? JSON.parse(ldataRaw) : null;
        const res = await suspendCreator(creator.user_id,ldata.user_id);

        if (!res.status) {
          this.toast.error(res.success || "Suspension failed");
          return;
        }

        this.toast.success("Creator suspended");

        this.loadStats();
        this.loadCreators();
      }
    }
  };
</script>


<style scoped>
  .admin-page {
    max-width: 1440px;
    margin: auto;
    padding: 48px;
  }

  .admin-header h1 {
    font-size: 36px;
    font-weight: 800;
  }

  .admin-header p {
    color: #64748b;
  }

  /* Filters */
  .filters {
    display: grid;
    grid-template-columns: 2fr 1fr 1fr;
    gap: 12px;
    margin: 32px 0;
  }

  .filters input,
  .filters select {
    padding: 12px 14px;
    border-radius: 12px;
    background: #f8fafc;
  }

  /* KPIs */
  .kpis {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 24px;
    margin-bottom: 32px;
  }

  .kpi {
    background: white;
    border-radius: 16px;
    padding: 20px;
    border: 1px solid #e5e7eb;
  }

  .kpi p {
    font-size: 11px;
    text-transform: uppercase;
    color: #94a3b8;
    font-weight: 700;
  }

  .kpi h2 {
    font-size: 26px;
    font-weight: 800;
  }

  .kpi h2.danger {
    color: #991b1b;
  }

  /* Table */
  .card {
    background: white;
    border-radius: 24px;
    padding: 32px;
    border: 1px solid #e5e7eb;
  }

  .creator-table {
    width: 100%;
    border-collapse: collapse;
  }

  .creator-table th,
  .creator-table td {
    padding: 14px;
    text-align: left;
    border-bottom: 1px solid #f1f5f9;
  }

  .sub {
    font-size: 12px;
    color: #94a3b8;
  }

  /* Pills */
  .status-pill {
    font-size: 11px;
    padding: 4px 10px;
    border-radius: 999px;
    font-weight: 800;
    text-transform: uppercase;
  }

  .status-pill.pending {
    background: #fffbeb;
    color: #92400e;
  }

  .status-pill.active {
    background: #ecfdf5;
    color: #047857;
  }

  .status-pill.suspended {
    background: #fef2f2;
    color: #991b1b;
  }

  .risk-pill {
    font-size: 11px;
    padding: 4px 10px;
    border-radius: 999px;
    font-weight: 800;
  }

  .risk-pill.low {
    background: #ecfdf5;
    color: #047857;
  }

  .risk-pill.medium {
    background: #fffbeb;
    color: #92400e;
  }

  .risk-pill.high {
    background: #fef2f2;
    color: #991b1b;
  }

  /* Actions */
  .actions {
    display: flex;
    gap: 8px;
  }

  .primary-btn {
    background: #0f172a;
    color: white;
    padding: 8px 14px;
    border-radius: 12px;
    font-weight: 700;
  }

  .ghost-btn {
    background: #f1f5f9;
    padding: 8px 14px;
    border-radius: 12px;
    font-weight: 700;
  }

  .danger-btn {
    background: #991b1b;
    color: white;
    padding: 8px 14px;
    border-radius: 12px;
    font-weight: 700;
  }

  /* Empty */
  .empty {
    padding: 24px;
    text-align: center;
    color: #64748b;
  }

  /* Mobile */
  @media (max-width: 900px) {
    .filters {
      grid-template-columns: 1fr;
    }

    .kpis {
      grid-template-columns: 1fr;
    }

    .admin-page {
      padding: 24px;
    }
  }

  /* ===============================
   ADMIN CREATORS – MOBILE PATCH
   =============================== */
  @media (max-width: 640px) {

    /* Page padding */
    .admin-page {
      padding: 20px 16px 72px;
    }

    /* Header */
    .admin-header h1 {
      font-size: 26px;
      line-height: 1.2;
    }

    .admin-header p {
      font-size: 14px;
    }

    /* Filters → stacked */
    .filters {
      gap: 10px;
      margin: 20px 0;
    }

    .filters input,
    .filters select {
      padding: 12px;
      font-size: 14px;
      border-radius: 10px;
    }

    /* KPIs → compact cards */
    .kpis {
      gap: 12px;
      margin-bottom: 24px;
    }

    .kpi {
      padding: 16px;
      border-radius: 14px;
    }

    .kpi h2 {
      font-size: 22px;
    }

    /* Card */
    .card {
      padding: 16px;
      border-radius: 18px;
    }

    /* Table → mobile card rows */
    .creator-table thead {
      display: none;
    }

    .creator-table,
    .creator-table tbody,
    .creator-table tr,
    .creator-table td {
      display: block;
      width: 100%;
    }

    .creator-table tr {
      padding: 14px 0;
      border-bottom: 1px solid #f1f5f9;
    }

    .creator-table td {
      padding: 6px 0;
      border: none;
      font-size: 14px;
    }

    .creator-table td strong {
      font-size: 15px;
    }

    .sub {
      font-size: 12px;
    }

    /* Pills spacing */
    .status-pill,
    .risk-pill {
      font-size: 10px;
      padding: 4px 8px;
    }

    /* Actions → full width */
    .actions {
      flex-direction: column;
      gap: 6px;
      margin-top: 8px;
    }

    .primary-btn,
    .ghost-btn,
    .danger-btn {
      width: 100%;
      text-align: center;
      padding: 10px 14px;
    }

    /* Pagination */
    .pagination {
      margin-top: 20px;
      display: flex;
      justify-content: space-between;
      align-items: center;
    }

    .page-info {
      font-size: 13px;
    }

    /* Empty state */
    .empty {
      font-size: 14px;
      padding: 20px 10px;
    }
  }
</style>